import React, { useState } from 'react';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Activity,
  Shield,
  Wifi,
  WifiOff
} from 'lucide-react';
import { toast } from 'sonner';
import { useGetWhatsappConfigQuery } from '@/client/api/whatsapp/config.query';
import { useGetWhatsappMetricsQuery } from '@/client/api/whatsapp/stats.query';
import { useGetWhatsappProfileQuery } from '@/client/api/whatsapp/profile.query';

const ENV_VAR_LABELS: Record<string, string> = {
  WHATSAPP_API_TOKEN: 'API Token',
  WHATSAPP_PHONE_NUMBER_ID: 'Phone Number ID',
  WHATSAPP_BUSINESS_ACCOUNT_ID: 'Business Account ID',
  WHATSAPP_APP_SECRET: 'App Secret',
  WHATSAPP_WEBHOOK_VERIFY_TOKEN: 'Webhook Verify Token',
  WHATSAPP_API_VERSION: 'API Version',
};

const CORE_KEYS = ['WHATSAPP_API_TOKEN', 'WHATSAPP_PHONE_NUMBER_ID', 'WHATSAPP_BUSINESS_ACCOUNT_ID'];

const SettingsPage: React.FC = () => {
  const { data: configData, isLoading: configLoading, refetch: refetchConfig } = useGetWhatsappConfigQuery();
  const { data: metricsData, isLoading: metricsLoading, refetch: refetchMetrics } = useGetWhatsappMetricsQuery();
  const { refetch: refetchProfile } = useGetWhatsappProfileQuery();

  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const config = configData?.data;
  const stats = metricsData && 'data' in metricsData ? metricsData.data : metricsData;

  const isFullyConfigured = config
    ? CORE_KEYS.every((key) => config[key as keyof typeof config]?.present)
    : false;

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const result = await refetchProfile();
      if (result.error) throw result.error;
      toast.success('Successfully connected to the WhatsApp Business API');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      await Promise.all([refetchConfig(), refetchMetrics()]);
      toast.success('Diagnostics refreshed');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to refresh diagnostics');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">WhatsApp Settings & Diagnostics</h1>
          <p className="text-sm text-gray-500 mt-1">Monitor WhatsApp integration health and configuration</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Connection Status
              </h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {isFullyConfigured ? (
                      <Wifi className="w-6 h-6 text-green-500" />
                    ) : (
                      <WifiOff className="w-6 h-6 text-red-500" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">WhatsApp Business API</p>
                      <p className="text-sm text-gray-500">
                        {configLoading ? 'Checking configuration...' : isFullyConfigured ? 'Core credentials configured' : 'Missing required credentials'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isFullyConfigured
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {isFullyConfigured ? 'Configured' : 'Incomplete'}
                    </span>
                    <button
                      onClick={handleTestConnection}
                      disabled={testing}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      {testing ? 'Testing...' : 'Test'}
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleSyncNow}
                  disabled={syncing}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Refreshing...' : 'Refresh Diagnostics'}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Environment Configuration
              </h2>

              <div className="space-y-2">
                {configLoading && <p className="text-sm text-gray-500">Loading configuration...</p>}
                {config && Object.entries(ENV_VAR_LABELS).map(([key, label]) => {
                  const status = config[key as keyof typeof config];
                  return (
                    <div key={key} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{label}</p>
                        {status?.present && status.value && (
                          <p className="text-xs text-gray-500 font-mono">{status.value}</p>
                        )}
                      </div>
                      {status?.present ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                          <CheckCircle className="w-3 h-3" /> Present
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-1 rounded-full">
                          <XCircle className="w-3 h-3" /> Missing
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Diagnostics</h3>

              {metricsLoading && <p className="text-sm text-gray-500">Loading...</p>}

              {stats && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Sent (last {stats.period_days}d)</span>
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.outbound.sent.toLocaleString()}</p>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Received (last {stats.period_days}d)</span>
                      <CheckCircle className="w-4 h-4 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.inbound.last_n_days.toLocaleString()}</p>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Delivery Rate</span>
                      <Activity className="w-4 h-4 text-purple-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.outbound.delivery_rate}%</p>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Failed Sends</span>
                      {stats.outbound.failed === 0 ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stats.outbound.failed.toLocaleString()}</p>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Total Opt-outs</span>
                    </div>
                    <p className="text-sm font-medium text-gray-700">{stats.optouts.total.toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">Security Notice</h4>
                  <p className="text-xs text-blue-800">
                    Credentials are managed via environment variables and are never fully displayed here. Rotate them directly in your deployment configuration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
